import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';

interface PmOfferProjectState {
  id: string;
  accountManagerId: string;
  accountSaleId: string;
  projectAuthorId: string;
  chiefTehnicianId: string;
  companyId: string;
  number: string;
  date: string;
  dateStart: string;
  dateEnd: string;
  enumPmOfferContractType: any;
  enumPmOfferContractTypeId: any;
  enumPmOfferStatus: any;
  enumPmOfferStatusId: any;
  metadata: any;
  remarks: any;
  accountManager: any;
  projectAuthor: any;
  accountSale: any;
  chiefTehnician: any;
  company: any;
  costPerKilometer: any;
  pmOfferIntervals: any[];
  pmOfferWorkPlaceItems: any[];
  interventionFileProjects: any[];
  pmOfferLaborExtras: any[];
  pmOfferCommentsTab: any[];
  location: any[];
  technician: any[];
  pmOfferWorkPlaceTransportCosts: any[];
  pmOfferSectionFiles: any[];
  equipmentTab: {
    [key: string]: any;
  };
  laborTab: {
    [key: string]: any;
  };
  props: {
    [key: string]: any;
  };
}

const currentDate = moment().format('YYYY-MM-DD');
const currentDateEnd = moment().add(1, 'year').format('YYYY-MM-DD');

const initialState: PmOfferProjectState = {
  id: '',
  accountManagerId: '',
  accountSaleId: '',
  projectAuthorId: '',
  chiefTehnicianId: '',
  companyId: '',
  enumPmOfferContractTypeId: 0,
  enumPmOfferStatusId: 0,
  number: '',
  date: currentDate,
  dateStart: currentDate,
  dateEnd: currentDateEnd,
  remarks: '',
  metadata: null,
  accountManager: null,
  projectAuthor: null,
  accountSale: null,
  chiefTehnician: null,
  company: null,
  enumPmOfferContractType: null,
  enumPmOfferStatus: null,
  costPerKilometer: 2.5,
  pmOfferIntervals: [],
  pmOfferWorkPlaceItems: [],
  pmOfferLaborExtras: [],
  pmOfferCommentsTab: [],
  location: [],
  technician: [],
  interventionFileProjects: [],
  pmOfferWorkPlaceTransportCosts: [],
  pmOfferSectionFiles: [],
  equipmentTab: { workPlaceId: null },
  laborTab: { laborPriceOnHour: 300 },
  props: { searchTerm: '', skip: 1, take: 10, totalItems: 0 },
};

const pmOfferProjectSlice = createSlice({
  name: 'pmOfferProject',
  initialState,
  reducers: {
    setPmOfferProjects: (state, action: PayloadAction<Partial<PmOfferProjectState>>) => {
      return { ...state, ...action.payload };
    },
    updateField: (state: any, action: PayloadAction<{ field: any; value: any }>) => {
      const { field, value } = action.payload;
      state[field] = value;
    },
    updateEquipmentTab: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      // Dynamically update equipmentTab with the specified field and value
      state.equipmentTab = {
        ...state.equipmentTab,
        [field]: value,
      };
    },
    updateLaborTab: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      // Dynamically update equipmentTab with the specified field and value
      state.laborTab = {
        ...state.laborTab,
        [field]: value,
      };
    },
    addPmOfferProjectWorkPlaceItem: (state, action) => {
      state.pmOfferWorkPlaceItems.push(action.payload);
    },
    updatePmOfferProjectWorkPlaceItem: (state, action: PayloadAction<{ index: number; updates: any }>) => {
      const { index, updates } = action.payload;
      if (state.pmOfferWorkPlaceItems[index]) {
        state.pmOfferWorkPlaceItems[index] = {
          ...state.pmOfferWorkPlaceItems[index],
          ...updates,
        };
      }
    },
    removePmOfferProjectWorkPlaceItem: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (index >= 0 && index < state.pmOfferWorkPlaceItems.length) {
        state.pmOfferWorkPlaceItems.splice(index, 1);
      }
    },
    // **Add interval actions**
    addPmOfferProjectInterval: (state, action: PayloadAction<any>) => {
      state.pmOfferIntervals.push(action.payload);
    },
    updatePmOfferProjectInterval: (state, action: PayloadAction<{ index: number; updates: Partial<any> }>) => {
      const { index, updates } = action.payload;
      if (state.pmOfferIntervals[index]) {
        state.pmOfferIntervals[index] = {
          ...state.pmOfferIntervals[index],
          ...updates,
        };
      }
    },
    removePmOfferProjectInterval: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (index >= 0 && index < state.pmOfferIntervals.length) {
        state.pmOfferIntervals.splice(index, 1);
      }
    },
    //
    addPmOfferProjectLaborExtra: (state, action) => {
      state.pmOfferLaborExtras.push(action.payload);
    },
    addPmOfferProjectComment: (state, action: PayloadAction<any>) => {
      state.pmOfferCommentsTab.push(action.payload);
    },
    updatePmOfferProjectComment: (
      state,
      action: PayloadAction<{
        departmentName: string;
        updates: Partial<any>;
      }>
    ) => {
      const { departmentName, updates } = action.payload;
      const index = state.pmOfferCommentsTab.findIndex((comment) => comment.departmentName === departmentName);

      if (index !== -1) {
        state.pmOfferCommentsTab[index] = {
          ...state.pmOfferCommentsTab[index],
          ...updates,
        };
      } else {
        state.pmOfferCommentsTab.push({
          departmentName,
          ...updates,
        });
      }
    },
    // Remove a file from a comment
    removePmOfferProjectComment: (
      state,
      action: PayloadAction<{
        departmentName: string;
        fileUid: string;
      }>
    ) => {
      const { departmentName, fileUid } = action.payload;
      const commentIndex = state.pmOfferCommentsTab.findIndex((comment) => comment.departmentName === departmentName);

      if (commentIndex !== -1) {
        const comment = state.pmOfferCommentsTab[commentIndex];

        const updatedFiles = (comment.pmContractCommentFiles || []).filter(
          (file: any) => file.uid !== fileUid && file.id !== fileUid
        );

        state.pmOfferCommentsTab[commentIndex] = {
          ...comment,
          pmContractCommentFiles: updatedFiles,
        };
      }
    },

    addPmOfferWorkPlaceTransportCost: (
      state: any,
      action: PayloadAction<{
        pmContractId: string;
        workPlaceId: string;
        kilometers: number;
        time: number;
        cost: number;
      }>
    ) => {
      state.pmContractWorkPlaceTransportCosts.push(action.payload);
    },

    // ✅ Update transport cost entry
    updatePmOfferWorkPlaceTransportCost: (
      state: any,
      action: PayloadAction<{
        pmContractId: string;
        workPlaceId: string;
        kilometers: number;
        time: number;
        cost: number;
      }>
    ) => {
      const { pmContractId, workPlaceId, kilometers, time, cost } = action.payload;
      const index = state.pmContractWorkPlaceTransportCosts.findIndex((item: any) => item.workPlaceId === workPlaceId);

      if (index !== -1) {
        // Update existing entry
        state.pmContractWorkPlaceTransportCosts[index] = {
          ...state.pmContractWorkPlaceTransportCosts[index],
          pmContractId,
          kilometers,
          time,
          cost,
        };
      } else {
        // Add new entry if it doesn't exist
        state.pmContractWorkPlaceTransportCosts.push({
          pmContractId,
          workPlaceId,
          kilometers,
          time,
          cost,
        });
      }
    },

    uploadFile: (state: any, action: PayloadAction<{ key: string; file: any }>) => {
      state[action.payload.key].push(action.payload.file);
    },
    deleteFile: (state: any, action: PayloadAction<{ key: string; uid: string }>) => {
      state[action.payload.key] = state[action.payload.key].filter(
        (file: any) => file.uid !== action.payload.uid && file.id !== action.payload.uid
      );
    },
    updateProps: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      state.props = {
        ...state.props,
        [field]: value,
      };
    },
    // Add a new section file
    updatePmOfferSectionFile: (state, action: PayloadAction<any>) => {
      const newFile = action.payload;

      // Generate a unique ID for the file if it doesn't have one
      if (!newFile.id) {
        newFile.id = '';
      }

      // Check if file already exists (by uid)
      const existingFileIndex = state.pmOfferSectionFiles.findIndex((file: any) => file.uid === newFile.uid);

      if (existingFileIndex !== -1) {
        // Update existing file
        state.pmOfferSectionFiles[existingFileIndex] = {
          ...state.pmOfferSectionFiles[existingFileIndex],
          ...newFile,
        };
      } else {
        // Add new file
        state.pmOfferSectionFiles.push(newFile);
      }
    },
    removePmOfferSectionFile: (state, action: PayloadAction<string>) => {
      const fileUid = action.payload;
      state.pmOfferSectionFiles = state.pmOfferSectionFiles.filter(
        (file: any) => file.uid !== fileUid && file.id !== fileUid
      );
    },
  },
});

export const {
  setPmOfferProjects,
  updateField,
  updateEquipmentTab, // Export the new action
  updateLaborTab,
  addPmOfferProjectWorkPlaceItem,
  updatePmOfferProjectWorkPlaceItem,
  removePmOfferProjectWorkPlaceItem,
  addPmOfferProjectInterval,
  updatePmOfferProjectInterval,
  removePmOfferProjectInterval,
  addPmOfferProjectLaborExtra,
  addPmOfferProjectComment,
  updatePmOfferProjectComment,
  removePmOfferProjectComment,
  addPmOfferWorkPlaceTransportCost,
  updatePmOfferWorkPlaceTransportCost,
  uploadFile,
  deleteFile,
  updateProps,
  updatePmOfferSectionFile,
  removePmOfferSectionFile,
} = pmOfferProjectSlice.actions;

export default pmOfferProjectSlice.reducer;
